import { useFormContext, useFieldArray } from "react-hook-form";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import RHFInput from "@/components/form/RHFInput";
import RHFSelect from "@/components/form/RHFSelect";
import RHFCheckbox from "@/components/form/RHFCheckbox";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { ANSWER_TYPE, ANSWER_TYPE_OPTIONS } from "@/config/dropdownData";

const INPUT_STYLE =
  "w-full border border-border-primary rounded-lg px-4 py-2.5 text-sm placeholder:text-text-muted bg-background-form/40 focus:outline-none transition text-text-primary";

// Keep in sync with MAX_QUESTION_LEN in the backend schema
// (server/app/api/v2/application_questions/schema.py) and the zod rule in
// ../../job/validation.schema.js.
const MAX_QUESTION_LEN = 200;

// Keep in sync with MAX_QUESTIONS_PER_JOB in the backend schema and the zod
// .max() rule in ../../job/validation.schema.js.
const MAX_QUESTIONS_PER_JOB = 20;

const NEW_QUESTION = {
  question: "",
  answerType: ANSWER_TYPE.SHORT_TEXT,
  isRequired: false,
};

/**
 * Manual screening questions configured on a job. Shared by every job flow —
 * manual create, the AI "Review & Update" step, edit and clone — because they
 * all render the same <JobForm>. No AI: the recruiter types each question.
 *
 * Reads/writes the `applicationQuestions` field-array on the surrounding form.
 * Display order is the array order; it is sent to the server as display_order
 * on submit (see services/jobService.js), so reordering here persists.
 *
 * Part of the Job feature (not a separate module) — always available; a
 * recruiter simply adds questions or leaves the list empty. Server-side
 * validation is authoritative.
 */
const ApplicationQuestionsEditor = ({ disabled = false }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "applicationQuestions",
  });

  const atMax = fields.length >= MAX_QUESTIONS_PER_JOB;

  return (
    <div className="col-span-1 sm:col-span-2 mt-2" data-field="applicationQuestions">
      <h2 className="text-[16px] font-bold text-text-primary mb-1">Application Questions</h2>
      <p className="text-xs text-text-muted mb-3">
        Screening questions candidates answer when they apply. Optional — up to {MAX_QUESTIONS_PER_JOB}. ({fields.length}/{MAX_QUESTIONS_PER_JOB})
      </p>

      {fields.length === 0 && <p className="text-sm text-text-muted mb-3">No questions added yet.</p>}

      <div className="space-y-3">
        {fields.map((item, index) => (
          <div key={item.id} className="border border-border-primary rounded-lg p-3 bg-background-form/40 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-text-secondary">Question {index + 1}</span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  disabled={disabled || index === 0}
                  onClick={() => move(index, index - 1)}
                  className="!p-1.5 bg-button-tertiary disabled:opacity-40"
                  aria-label="Move question up"
                >
                  <ChevronUp size={16} />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={disabled || index === fields.length - 1}
                  onClick={() => move(index, index + 1)}
                  className="!p-1.5 bg-button-tertiary disabled:opacity-40"
                  aria-label="Move question down"
                >
                  <ChevronDown size={16} />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={disabled}
                  onClick={() => remove(index)}
                  className="!p-1.5 bg-button-tertiary text-red-400 hover:text-red-500"
                  aria-label="Remove question"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>

            <RHFInput
              name={`applicationQuestions.${index}.question`}
              label={`Question (max ${MAX_QUESTION_LEN} characters)`}
              placeholder="e.g. Do you have a valid work visa?"
              inputClass={INPUT_STYLE}
              maxLength={MAX_QUESTION_LEN}
              maxLengthMessage={`Question must be ${MAX_QUESTION_LEN} characters or fewer`}
              disabled={disabled}
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <RHFSelect name={`applicationQuestions.${index}.answerType`} label="Answer Type" options={ANSWER_TYPE_OPTIONS} disabled={disabled} required />
              <div className="pb-2.5">
                <RHFCheckbox name={`applicationQuestions.${index}.isRequired`} label="Required (candidate must answer)" disabled={disabled} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {typeof errors?.applicationQuestions?.message === "string" && <p className="text-red-400 text-xs mt-2">{errors.applicationQuestions.message}</p>}

      <Button
        type="button"
        onClick={() => append({ ...NEW_QUESTION })}
        disabled={disabled || atMax}
        className={cn(
          "mt-3 !px-4 py-2 text-sm rounded-lg border-none transition flex items-center gap-2",
          "text-text-base bg-button-secondary",
          (disabled || atMax) && "opacity-50 cursor-not-allowed",
        )}
      >
        <Plus size={16} /> Add Question
      </Button>
      {atMax && <p className="text-xs text-text-muted mt-2">Maximum of {MAX_QUESTIONS_PER_JOB} questions reached.</p>}
    </div>
  );
};

export default ApplicationQuestionsEditor;
